import { Bar, BarChart, CartesianGrid, XAxis, LabelList, Cell } from 'recharts'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/components/ui/chart'
import { Skeleton } from '@/shared/components/ui/skeleton'

interface SolicitudesPorProvinciaChartProps {
  data: Record<string, number> | undefined
  isLoading?: boolean
}

const chartConfig = {
  cantidad: {
    label: 'Solicitudes',
    color: '#14b8a6',
  },
} satisfies ChartConfig

export function SolicitudesPorProvinciaChart({ data, isLoading }: SolicitudesPorProvinciaChartProps) {
  if (isLoading) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    )
  }

  if (!data || Object.keys(data).length === 0) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle>Solicitudes por Provincia</CardTitle>
          <CardDescription>Distribucion geografica</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">
            No hay datos disponibles
          </div>
        </CardContent>
      </Card>
    )
  }

  // Convertir datos y ordenar por cantidad descendente
  const chartData = Object.entries(data)
    .map(([provincia, cantidad]) => ({
      provincia,
      cantidad,
    }))
    .sort((a, b) => b.cantidad - a.cantidad)

  // Tonos teal de oscuro a claro
  const tealShades = ['#0d9488', '#14b8a6', '#2dd4bf', '#5eead4', '#99f6e4', '#b2f5ea', '#ccfbf1']

  const getBarColor = (index: number) => {
    const i = Math.min(index, tealShades.length - 1)
    return tealShades[i]
  }

  return (
    <Card className="flex flex-col border border-gray-300 dark:border-gray-700">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Solicitudes Por Provincia</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 p-2">
        <ChartContainer config={chartConfig} className="!aspect-auto w-full h-[350px]">
          <BarChart accessibilityLayer data={chartData} margin={{ top: 20 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="provincia"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              fontSize={9}
              interval={0}
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
            />
            <Bar dataKey="cantidad" radius={6}>
              {chartData.map((_, index) => (
                <Cell key={index} fill={getBarColor(index)} />
              ))}
              <LabelList dataKey="cantidad" position="top" fontSize={11} />
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
